// =============================================================================
// AssessmentFrameworkIndicator.tsx
// =============================================================================
// Fase 2.6 Blokk E — visuell indikator for kompatibilitet mot studieplanens
// vurderingsramme. Brukes i ExamCreator (exam_set) og LmsMappingModal
// (lms_mapping). Live preview mot /api/study-plans/:id/framework-check.
// =============================================================================

import { useEffect, useState } from 'react';
import { CheckCircle2, AlertTriangle, XCircle, Info, Loader2 } from 'lucide-react';
import { authenticatedFetch } from '@/lib/auth';
import HelpTooltip from './help/HelpTooltip';

export interface CompatResult {
  compatible: boolean;
  enforced: boolean;
  mismatches: string[];
  warnings: string[];
  flexibility: 'strict' | 'equivalent_allowed' | 'free' | null;
  blocking: boolean;
  requires_rationale: boolean;
  requires_signoff: boolean;
}

interface Props {
  studyPlanId: string | null;
  implementationType: 'exam_set' | 'lms_mapping';
  implementationData: Record<string, any>;
  rationale: string;
  onRationaleChange: (text: string) => void;
  onResultChange?: (result: CompatResult | null) => void;
}

const MIN_RATIONALE = 50;

export default function AssessmentFrameworkIndicator({
  studyPlanId,
  implementationType,
  implementationData,
  rationale,
  onRationaleChange,
  onResultChange,
}: Props) {
  const [result, setResult] = useState<CompatResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Debounced live-sjekk
  useEffect(() => {
    if (!studyPlanId) {
      setResult(null);
      onResultChange?.(null);
      return;
    }

    let cancelled = false;
    const timer = setTimeout(async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await authenticatedFetch(
          `/api/study-plans/${studyPlanId}/framework-check`,
          {
            method: 'POST',
            body: JSON.stringify({
              implementation_type: implementationType,
              implementation_data: implementationData,
            }),
          }
        );
        if (cancelled) return;
        const data = await res.json();
        if (!res.ok || !data.success) {
          setError(data.error || 'Kunne ikke sjekke kompatibilitet');
          setResult(null);
          onResultChange?.(null);
        } else {
          setResult(data.result);
          onResultChange?.(data.result);
        }
      } catch (err: any) {
        if (!cancelled) {
          setError(err.message);
          setResult(null);
          onResultChange?.(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 300);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [studyPlanId, implementationType, JSON.stringify(implementationData)]);

  if (!studyPlanId) return null;

  if (loading && !result) {
    return (
      <div className="flex items-center gap-2 text-sm text-gray-500 p-3">
        <Loader2 size={14} className="animate-spin" />
        Sjekker kompatibilitet…
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-gray-50 border border-gray-200 rounded-md p-3 text-sm text-gray-600">
        {error}
      </div>
    );
  }

  if (!result) return null;

  // NULL framework eller FREE = ingen håndhevelse
  if (!result.enforced) {
    return null;  // Ikke vis noe — stille pass-through
  }

  // Kompatibel + ingen warnings = grønn OK
  if (result.compatible && result.warnings.length === 0) {
    return (
      <div className="bg-green-50 border border-green-200 rounded-md p-3 flex gap-2 items-start">
        <CheckCircle2 size={16} className="text-green-600 mt-0.5 flex-shrink-0" />
        <div className="text-sm text-green-900">
          <div className="font-medium">Matcher studieplanens vurderingsramme</div>
          <div className="text-xs text-green-700 mt-1">
            Fleksibilitet: {flexibilityLabel(result.flexibility)}
          </div>
        </div>
      </div>
    );
  }

  // Blocking (strict + mismatch) = rød
  if (result.blocking) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-md p-3 flex gap-2 items-start">
        <XCircle size={16} className="text-red-600 mt-0.5 flex-shrink-0" />
        <div className="text-sm text-red-900 flex-1">
          <div className="font-medium">Blokkeres av studieplanens vurderingsramme</div>
          <div className="text-xs text-red-800 mt-1">
            Studieplanen er satt til <strong>streng</strong> fleksibilitet. Endre valgene
            under for å matche rammen, eller be studieplan-eier endre fleksibiliteten til
            "fleksibel" slik at avvik tillates med begrunnelse.
          </div>
          {result.mismatches.length > 0 && (
            <ul className="text-xs text-red-800 mt-2 list-disc list-inside space-y-1">
              {result.mismatches.map((m, i) => <li key={i}>{m}</li>)}
            </ul>
          )}
        </div>
      </div>
    );
  }

  // Requires rationale (equivalent + mismatch) = gul
  if (result.requires_rationale) {
    const len = rationale.trim().length;
    const metMin = len >= MIN_RATIONALE;
    return (
      <div className="bg-amber-50 border border-amber-200 rounded-md p-3 space-y-3">
        <div className="flex gap-2 items-start">
          <AlertTriangle size={16} className="text-amber-600 mt-0.5 flex-shrink-0" />
          <div className="text-sm text-amber-900 flex-1">
            <div className="font-medium">Avvik fra studieplanens vurderingsramme</div>
            <div className="text-xs text-amber-800 mt-1">
              Studieplanen tillater avvik med begrunnelse. Begrunnelsen lagres på attesten
              og er synlig i revisjonssporet.
            </div>
            {result.mismatches.length > 0 && (
              <ul className="text-xs text-amber-800 mt-2 list-disc list-inside space-y-1">
                {result.mismatches.map((m, i) => <li key={i}>{m}</li>)}
              </ul>
            )}
          </div>
        </div>
        <div>
          <label className="flex items-center gap-1 text-xs font-medium text-amber-900 mb-1">
            Begrunnelse for avvik * (minst {MIN_RATIONALE} tegn)
            <HelpTooltip slug="framework-deviation" size="sm" />
          </label>
          <textarea
            className={`w-full border rounded-md p-2 text-sm ${
              metMin ? 'border-green-300 bg-white' : 'border-amber-300 bg-white'
            }`}
            rows={3}
            value={rationale}
            onChange={(e) => onRationaleChange(e.target.value)}
            placeholder="F.eks. 'Vi bruker observasjon under praksis i stedet for praktisk prøve fordi…'"
          />
          <div className={`text-xs mt-1 ${metMin ? 'text-green-700' : 'text-amber-700'}`}>
            {len}/{MIN_RATIONALE} tegn {metMin && '✓'}
          </div>
        </div>
      </div>
    );
  }

  // Requires signoff (LMS capability-advarsel) = blå
  if (result.requires_signoff && result.warnings.length > 0) {
    return (
      <div className="bg-blue-50 border border-blue-200 rounded-md p-3 flex gap-2 items-start">
        <Info size={16} className="text-blue-600 mt-0.5 flex-shrink-0" />
        <div className="text-sm text-blue-900 flex-1">
          <div className="font-medium">Krever signert admin-bekreftelse</div>
          <div className="text-xs text-blue-800 mt-1">
            LMS-et eksponerer ikke metadata som verifiserer vurderingsrammen. Admin må
            bekrefte at avtalen med LMS-leverandøren likevel oppfyller kravene.
          </div>
          {result.warnings.length > 0 && (
            <ul className="text-xs text-blue-800 mt-2 list-disc list-inside space-y-1">
              {result.warnings.map((w, i) => <li key={i}>{w}</li>)}
            </ul>
          )}
        </div>
      </div>
    );
  }

  // Fallback: compatible men har warnings
  return (
    <div className="bg-blue-50 border border-blue-200 rounded-md p-3 flex gap-2 items-start">
      <Info size={16} className="text-blue-600 mt-0.5 flex-shrink-0" />
      <div className="text-sm text-blue-900 flex-1">
        <ul className="text-xs list-disc list-inside space-y-1">
          {result.warnings.map((w, i) => <li key={i}>{w}</li>)}
        </ul>
      </div>
    </div>
  );
}

function flexibilityLabel(flex: CompatResult['flexibility']): string {
  switch (flex) {
    case 'strict': return 'streng';
    case 'equivalent_allowed': return 'fleksibel (avvik med begrunnelse)';
    case 'free': return 'fri (ingen håndhevelse)';
    default: return 'ikke satt';
  }
}
